import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface QuoteRow {
  id: string;
  tenant_id: string;
  lead_id: string | null;
  quote_no: string;
  title: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  valid_until: string | null;
  total_amount: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
}

export interface QuoteLineRow {
  id: string;
  quote_id: string;
  tenant_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  position: number;
  created_at: string;
}

export interface UseQuotesOptions {
  search?: string;
  status?: QuoteRow['status'];
  limit?: number;
}

export function useQuotes(supabase: SupabaseClient, tenantId: string, opts: UseQuotesOptions = {}) {
  return useQuery({
    queryKey: ['quotes', tenantId, opts],
    queryFn: async () => {
      let q = supabase
        .from('crm_quotes')
        .select('*', { count: 'exact' })
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(opts.limit ?? 50);
      if (opts.status) q = q.eq('status', opts.status);
      if (opts.search) q = q.ilike('title', `%${opts.search}%`);
      const { data, count, error } = await q;
      if (error) throw error;
      return { data: data as QuoteRow[], total: count ?? 0 };
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useCreateQuote(supabase: SupabaseClient) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      tenant_id: string;
      title: string;
      lead_id?: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.from('crm_quotes').insert(payload).select().single();
      if (error) throw error;
      return data as QuoteRow;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes', vars.tenant_id] });
    },
  });
}

export function useUpdateQuoteStatus(supabase: SupabaseClient) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      tenantId: _tenantId,
      client_note,
    }: {
      id: string;
      status: QuoteRow['status'];
      tenantId: string;
      client_note?: string | null;
    }) => {
      void _tenantId;
      void client_note;
      const extra: Record<string, string> = {};
      if (status === 'sent') extra.sent_at = new Date().toISOString();
      if (status === 'accepted') extra.accepted_at = new Date().toISOString();
      if (status === 'rejected') extra.rejected_at = new Date().toISOString();
      const { error } = await supabase
        .from('crm_quotes')
        .update({ status, ...extra })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes', vars.tenantId] });
    },
  });
}
